import { ResolveFn, Router } from '@angular/router';
import { QuotationService } from '../../services/quotation/quotation.service';
import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QuotationDetails } from '../../models/quotation';
import { forkJoin, map, switchMap, tap } from 'rxjs';
import { RFQGroup } from '../../models/rfq';
import { ApiResponse } from '../../Interfaces/apiResponse.interface';
import { NavigationPipe } from '../../pipes/navigation/navigation.pipe';
import { Path } from '../../constants/paths.constant';
import { RfqService } from '../../services/rfq/rfq.service';

interface QuotationDetailsResolvedData {
  details: ApiResponse<QuotationDetails>;
  groups: ApiResponse<RFQGroup[]>;
}

export const quotationDetailsResolver: ResolveFn<
  QuotationDetailsResolvedData
> = (route, state) => {
  const router = inject(Router);
  const rfqService = inject(RfqService);
  const navigation = new NavigationPipe();
  const quotationService = inject(QuotationService);
  const destroyRef = inject(DestroyRef);
  const rfqId = route.params['rfqId'];
  const quotationId = route.params['quotationId'];
  const isBuyer = state.url.includes('buyer');

  const redirectIfEmpty = tap(
    ({ details, groups }: QuotationDetailsResolvedData) => {
      if (!details.data || !groups.data)
        router.navigate([
          navigation.transform(
            isBuyer
              ? `${Path.BUYER}/${Path.QUOTATION.QUOTATION_RFQ}`
              : `${Path.SUPPLIER}/${Path.QUOTATION.QUOTATION_RFQ}`
          ),
        ]);
    }
  );

  /*
   * `GET /rfqs/{rfq}/quotations/{quotation}` and its `/groups` twin are
   * deprecated in favor of `GET /rfqs/{rfq}` (RFQ summary) and
   * `GET /rfqs/{rfq}/quotation-details` (every group with its quotations) —
   * the same pair `quotation-review.component.ts` already reads from. This
   * quotation belongs to exactly one group under that shape, so it is found
   * by scanning the groups for it rather than asked for by id.
   */
  if (isBuyer) {
    return forkJoin({
      rfq: rfqService.getRFQById(rfqId),
      reviewGroups: quotationService.getBuyerRfqQuotationDetails(rfqId),
    }).pipe(
      map(({ rfq, reviewGroups }) => {
        const numericQuotationId = Number(quotationId);
        const group = (reviewGroups.data ?? []).find(candidate =>
          candidate.quotations?.some(
            quotation => quotation.id === numericQuotationId
          )
        );
        const quotation = group?.quotations?.find(
          quotation => quotation.id === numericQuotationId
        );
        const rfqData = rfq.rfq;

        if (!group || !quotation || !rfqData) {
          return {
            details: {} as ApiResponse<QuotationDetails>,
            groups: {} as ApiResponse<RFQGroup[]>,
          };
        }

        return {
          details: {
            data: {
              quotation: {
                id: quotation.id,
                rfq_id: rfqData.id,
                group_id: group.id,
                supplier: quotation.supplier_name,
                buyer: '',
                state: quotation.state,
                total_price: quotation.total_price,
                rfq: rfqData,
              },
              rfq: rfqData,
            },
          } as ApiResponse<QuotationDetails>,
          groups: {
            data: [
              {
                id: group.id,
                items_count: group.items_count ?? null,
                service: group.service,
                items: quotation.items ?? [],
                is_general_service: group.is_general_service,
                description: group.description ?? undefined,
                state: group.state,
                deadline: group.deadline,
                matched_suppliers_count: group.matched_suppliers_count,
              },
            ],
          } as ApiResponse<RFQGroup[]>,
        };
      }),
      takeUntilDestroyed(destroyRef),
      redirectIfEmpty
    );
  }

  /*
   * A supplier quotation is now read through its group. The group comes from
   * the route wherever the link knew it; otherwise the supplier's groups on
   * this RFQ settle it, which is unambiguous whenever they hold a single
   * invite there. The groups are needed for the screen either way, so the
   * two calls run in sequence rather than costing an extra request.
   */
  return rfqService.getSupplierReceivedRfqGroups(rfqId).pipe(
    switchMap(groups => {
      const groupId =
        Number(route.params['groupId']) ||
        (groups.data?.length === 1 ? groups.data[0].id : 0);

      return quotationService
        .getSupplierQuotationDetails(rfqId, groupId, quotationId)
        .pipe(map(details => ({ details, groups })));
    }),
    takeUntilDestroyed(destroyRef),
    redirectIfEmpty
  );
};
